/**
 * One reading of "this query failed", shared by every surface that has to say so.
 *
 * BEN-141 QF4. The forced-state pass of the QA sweep
 * (run 20260908T224035242Z-8dc5) graded eighteen signed-in routes silent when
 * their own data came back 500 or unreadable, and blank when it came back 403.
 * The cause was not eighteen missing error branches; it was that nothing in the
 * app turned a query error into a sentence. This module is that one step, and
 * `components/QueryErrorNotice.tsx` is the one surface that renders it.
 *
 * The distinctions here are the ones a user can act on, and no more: a refusal
 * they cannot do anything about (403), a session that has ended and can be
 * fixed by signing in (401), a fault on the server's side (5xx), an answer that
 * arrived and could not be read, and a request that never reached the server.
 */
import { useSyncExternalStore } from "react";
import type { Query, QueryClient, QueryKey } from "@tanstack/react-query";
import { LegalAcceptanceRequired, MalformedResponse, SubscriptionRequired } from "@/api/errors";

export type QueryFailureKind =
  | "forbidden"
  | "session-expired"
  | "rate-limited"
  | "server"
  | "unreadable"
  | "offline"
  | "unknown";

interface ResponseShape {
  status?: number;
  data?: { code?: unknown; detail?: unknown } | unknown;
}

function responseOf(error: unknown): ResponseShape | undefined {
  const response = (error as { response?: ResponseShape } | null)?.response;
  return response && typeof response === "object" ? response : undefined;
}

function bodyCode(error: unknown): unknown {
  const data = responseOf(error)?.data;
  return data && typeof data === "object" ? (data as { code?: unknown }).code : undefined;
}

export function queryFailureKind(error: unknown): QueryFailureKind {
  if (error instanceof MalformedResponse) return "unreadable";

  const status = responseOf(error)?.status;
  if (status === undefined) {
    // axios reports a request that never got an answer with no `response` at
    // all. Everything else without a status is genuinely unclassified.
    const code = (error as { code?: unknown } | null)?.code;
    return code === "ERR_NETWORK" || code === "ECONNABORTED" ? "offline" : "unknown";
  }
  if (status === 401) return "session-expired";
  if (status === 403) {
    return bodyCode(error) === "session_expired" ? "session-expired" : "forbidden";
  }
  // 429 is the one 4xx a user can actually clear by waiting, and the dev API
  // hands it out at 1000 requests an hour per account. Reported as its own
  // thing so it does not read as an outage.
  if (status === 429) return "rate-limited";
  if (status >= 500) return "server";
  return "unknown";
}

export interface QueryFailureCopy {
  title: string;
  body: string;
  action: "retry" | "sign-in";
}

/**
 * The sentences themselves.
 *
 * Every one of them has to survive two readers. A person needs to know what is
 * missing and what to do next. The sweep reads the rendered text and grades a
 * surface silent unless it matches its own vocabulary — `saysError` on
 * /error|went wrong|failed|unable to|try again|couldn.t load|problem/ and
 * `saysDenied` on /denied|forbidden|not authoriz|no access|permission/
 * (`bin/sweep.mjs`). Those words are therefore chosen, not incidental, and
 * `queryFailureSurface.test.tsx` pins them.
 */
export function queryFailureCopy(kind: QueryFailureKind, area?: string): QueryFailureCopy {
  const what = area ?? "part of this page";
  switch (kind) {
    case "forbidden":
      return {
        title: "You do not have access to this",
        body: `Your account does not have permission to see ${what}. If that looks wrong, ask us through Help and support.`,
        action: "retry",
      };
    case "session-expired":
      return {
        title: "Your session has ended",
        body: "Sign in again to pick up where you left off.",
        action: "sign-in",
      };
    case "rate-limited":
      return {
        title: "We could not load this",
        body: `The server is limiting how often this account can ask for ${what}. Wait a minute, then try again.`,
        action: "retry",
      };
    case "server":
      return {
        title: "We could not load this",
        body: `The server ran into a problem while sending ${what}. Try again in a moment.`,
        action: "retry",
      };
    case "unreadable":
      return {
        title: "We could not load this",
        body: `The server answered with something we could not read, so ${what} is missing rather than empty. Try again.`,
        action: "retry",
      };
    case "offline":
      return {
        title: "We could not reach the server",
        body: `The request for ${what} never got an answer. Check your connection and try again.`,
        action: "retry",
      };
    default:
      return {
        title: "We could not load this",
        body: `Something went wrong while loading ${what}. Try again.`,
        action: "retry",
      };
  }
}

/**
 * Deliberate refusals that already own a screen are not load failures.
 *
 * 402 is the paywall (`SubscriptionGate`) and 409 is the legal-acceptance
 * dialog. Both are the product working. Describing them a second time as
 * "we could not load this" would report a correct app as broken, which is the
 * mirror image of the defect this module exists for.
 */
export function isReportableQueryFailure(error: unknown): boolean {
  if (error == null) return false;
  if (error instanceof SubscriptionRequired) return false;
  if (error instanceof LegalAcceptanceRequired) return false;
  const status = responseOf(error)?.status;
  return status !== 402 && status !== 409;
}

/**
 * A human name for the data a query carries, from its key.
 *
 * Keyed on the first segment because that is what every hook in `hooks/` keys
 * on. An unknown key gets the honest generic rather than a guess spun out of
 * the identifier.
 */
const AREA_BY_KEY: Record<string, string> = {
  bankAccounts: "your connected banks",
  "budget-summary": "your budget",
  calendar: "your calendar",
  categories: "your categories",
  "category-rules": "your categorisation rules",
  dashboard: "your dashboard",
  debts: "your debts",
  "financial-analysis": "your financial analysis",
  "forecast-periods": "your forecast",
  "forecast-plan": "your forecast",
  goals: "your goals",
  insights: "your insights",
  "pay-periods": "your pay periods",
  paycheck: "your paycheck",
  profile: "your account",
  subscription: "your subscription",
  transactions: "your transactions",
  transactionSummary: "your transaction summary",
  unknownTransactions: "your uncategorised transactions",
};

export function queryFailureArea(key: QueryKey): string {
  const [head, next] = key as unknown[];
  if (head === "dashboard" && next === "alerts") return "your alerts";
  if (typeof head === "string" && AREA_BY_KEY[head]) return AREA_BY_KEY[head];
  return "part of this page";
}

export interface QueryFailure {
  queryHash: string;
  queryKey: QueryKey;
  error: unknown;
  area: string;
}

/**
 * A query whose failure a page must own up to.
 *
 * Three conditions, each of which removes a class of false alarm rather than a
 * class of defect:
 *
 * - `data === undefined`: a refetch that failed while the previous answer is
 *   still on screen has not left the user reading nothing, so it belongs in a
 *   quieter place than a page-level alert.
 * - at least one observer: an unmounted query's stale error is not about the
 *   page in front of the user.
 * - `meta.failureNotice !== false`: the opt-out for a query whose own component
 *   has already decided what a failure means. `useSubscriptionStatus` is the
 *   one that uses it, because the gate deliberately fails open.
 */
function isReportable(query: Query): boolean {
  if (query.state.status !== "error") return false;
  if (query.state.data !== undefined) return false;
  if (query.getObserversCount() === 0) return false;
  if (query.meta?.failureNotice === false) return false;
  return isReportableQueryFailure(query.state.error);
}

const snapshots = new WeakMap<QueryClient, { signature: string; value: QueryFailure[] }>();
const NONE: QueryFailure[] = [];

function snapshotOf(client: QueryClient): QueryFailure[] {
  const failures = client
    .getQueryCache()
    .getAll()
    .filter(isReportable)
    .map((query) => ({
      queryHash: query.queryHash,
      queryKey: query.queryKey,
      error: query.state.error,
      area: queryFailureArea(query.queryKey),
    }));

  // useSyncExternalStore compares snapshots by reference and re-reads on every
  // cache event, of which a busy page has hundreds. The signature is what keeps
  // the identity stable across all the events that changed nothing here.
  const signature = failures.map((f) => `${f.queryHash}:${queryFailureKind(f.error)}`).join("|");
  const previous = snapshots.get(client);
  if (previous && previous.signature === signature) return previous.value;
  const value = failures.length === 0 ? NONE : failures;
  snapshots.set(client, { signature, value });
  return value;
}

/** Every currently-observed query that failed with nothing to show for it. */
export function useQueryFailures(client: QueryClient): QueryFailure[] {
  const cache = client.getQueryCache();
  return useSyncExternalStore(
    (onChange) => cache.subscribe(onChange),
    () => snapshotOf(client),
    () => NONE,
  );
}
